// Vendor Libs
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
    get,
    includes,
    indexOf,
    isArray,
    isEmpty,
    isFunction,
    isPlainObject,
    isString,
    last,
    map,
    omit,
    pick,
    startCase,
    toString,
    uniqueId,
} from 'lodash';

// App Libs
import formatters from '../../lib/app/formatters';
import {formatDate, formatDuration, formatTimezone} from '../../lib/core/date';

// Components
import AccordionPanel from './accordion-panel';
import PanelAction from './panel-action';

// Config
import recordDetailsConfig from '../../../config/record-details-config';

// Styles
import './record-details.scss';

export default class RecordDetails extends Component {
    state = {
        showAllFields: false,
    };

    getClass() {
        const {className} = this.props;

        return classnames(
            {
                'record-details': true,
            },
            className
        );
    }

    getPanelActionProps() {
        const {detailExpandIcon} = this.props;

        return {
            groupByIcon: true,
            hotkey: 'd',
            icon: detailExpandIcon || 'list',
            onClick: this.handleShowAllClick,
            title: 'Show All Fields',
            weight: 90,
        };
    }

    getSearchData() {
        const {showAllFields} = this.state;
        const {searchData, whitelistKeys} = this.props;

        return whitelistKeys && !showAllFields
            ? map(searchData, (searchRecord) => pick(searchRecord, whitelistKeys))
            : searchData;
    }

    handleShowAllClick = (event) => {
        event && event.preventDefault && event.preventDefault();

        this.setState((prevState) => ({showAllFields: !prevState.showAllFields}));
    };

    formatLabel(key) {
        const {noTitleFormatting} = this.props;
        const {label} = get(recordDetailsConfig, ['keyTypes', key], {});

        return noTitleFormatting ? key : label || startCase(last(toString(key).split('.')));
    }

    shouldDefaultExpand(key) {
        const {defaultExpandKeys} = this.props;

        return defaultExpandKeys === true || includes(defaultExpandKeys, key);
    }

    shouldRenderAccordion(detail) {
        const {showAllFields} = this.state;

        return (isPlainObject(detail) || isArray(detail)) && !(showAllFields && isEmpty(detail));
    }

    shouldRenderDetail(detail, detailKey) {
        const {showAllFields} = this.state;
        const {blacklistKeys} = this.props;

        return (showAllFields || detail !== null) && indexOf(blacklistKeys || [], detailKey) < 0;
    }

    renderAccordionDetail(detail, key, label, isPrimaryList) {
        const {showAllFields} = this.state;
        const props = {
            defaultExpanded: this.shouldDefaultExpand(key),
            detailsRenderer: () => this.renderDetailItem(detail, key),
            toggleContent: this.renderDetailLabel(key, label || key, 'details--title_accordion', isPrimaryList),
            togglePosition: 'right',
        };

        return showAllFields || !isEmpty(detail) ? <AccordionPanel {...props} /> : null;
    }

    renderContent() {
        const searchData = this.getSearchData();

        return searchData.length ? this.renderRecord(searchData) : null;
    }

    renderDefaultDetail(detail, key, label, isPrimaryList) {
        const {detailStartRenderer} = this.props;
        const elementId = detailStartRenderer ? uniqueId('record-details_') : undefined;

        return (
            <div className="details--row">
                {this.renderDetailStart(detail, key, label, elementId)}
                {this.renderDetailLabel(key, label || key, null, isPrimaryList, elementId)}
                {this.renderDetailItem(detail, key)}
            </div>
        );
    }

    renderDetail = (detail, key, label, isPrimaryList) => {
        let content = null;

        if (this.shouldRenderDetail(detail, key)) {
            if (this.shouldRenderAccordion(detail)) {
                content = this.renderAccordionDetail(detail, key, label, isPrimaryList);
            } else {
                content = this.renderDefaultDetail(detail, key, label, isPrimaryList);
            }

            content = (
                <li className="details--item" key={key}>
                    {content}
                </li>
            );
        }

        return content;
    };

    renderDetailItem = (detail, key) => {
        const {keyRenderers} = this.props;
        const keyRenderer = get(keyRenderers, key);
        let content;
        let title;
        const classes = {'details--description': true};

        if (isArray(detail) || isPlainObject(detail)) {
            if (!isEmpty(detail)) {
                classes['details--description_nested'] = true;
            } else {
                detail = 'N/A'; // eslint-disable-line no-param-reassign
            }
        }

        if (keyRenderer) {
            content = keyRenderer(detail, key);
        } else if (isArray(detail)) {
            content = map(detail, (detail, key) => this.renderList(detail, key));
        } else if (isPlainObject(detail)) {
            content = this.renderList(detail);
        } else {
            content = this.renderDetailItemContent(detail, key) || 'N/A';
            title = this.renderDetailItemTitle(detail, key);
        }

        return (
            <div className={classnames(classes)} title={title}>
                {content}
            </div>
        );
    };

    renderDetailItemContent(detail, key) {
        const {type, format} = get(recordDetailsConfig, ['keyTypes', key], {});
        const formatting = {
            date: formatDate,
            datetime: formatTimezone,
            duration: formatDuration,
        };

        return (formatting[type] && formatting[type](detail, format)) || toString(detail);
    }

    renderDetailItemTitle(detail, key) {
        const {type, format} = get(recordDetailsConfig, ['keyTypes', key], {});
        let title = null;

        if (type === 'datetime') {
            title = formatTimezone(detail, undefined, 'UTC');
        } else if (type === 'date' && format !== 'L') {
            title = formatDate(detail, 'L');
        }

        return title;
    }

    renderDetailLabel(key, label, className, isPrimaryList, elementId) {
        const {onClick} = this.props;

        return (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
            <label
                className={classnames(className, 'details--title', {
                    'details--title_clickable': isPrimaryList && isFunction(onClick),
                })}
                htmlFor={elementId}
                onClick={isPrimaryList && isFunction(onClick) ? (event) => onClick(event, key) : null}
            >
                {label}:
            </label>
        );
    }

    renderDetailStart(detail, key, label, elementId) {
        const {detailStartRenderer} = this.props;

        return detailStartRenderer ? detailStartRenderer(detail, key, label, elementId) : null;
    }

    renderHeader() {
        const {headerRenderer, searchData} = this.props;

        return headerRenderer ? headerRenderer(searchData) : null;
    }

    renderFooter() {
        const {footerRenderer, searchData} = this.props;

        return footerRenderer ? footerRenderer(searchData) : null;
    }

    renderList = (details, key, isPrimaryList) => (
        <ul className="details--list" key={key}>
            {isString(details)
                ? this.renderDetail(details, key, this.formatLabel(key))
                : this.renderListOrdered(details, isPrimaryList)}
        </ul>
    );

    renderListOrdered(details, isPrimaryList) {
        const {showAllFields} = this.state;
        const {keyOrder: keyOrderProp} = this.props;
        const keyOrder = get(recordDetailsConfig, ['keyOrders', keyOrderProp], []);
        const baseKeyOrder = [];
        const orderedDetails = isPrimaryList
            ? map(keyOrder, (item) => {
                  let detail;
                  let key;
                  let label;

                  if (isString(item)) {
                      detail = get(details, item);
                      key = item;
                      label = this.formatLabel(item);
                  } else if (item.key) {
                      detail = get(details, item.key);
                      detail = item.formatter
                          ? isFunction(formatters[item.formatter])
                              ? formatters[item.formatter](detail, details)
                              : detail
                          : detail;
                      key = item.key; // eslint-disable-line prefer-destructuring
                      label = item.label ? item.label : this.formatLabel(item.key);
                  }

                  if (!includes(key, '.')) {
                      baseKeyOrder.push(key);
                  }

                  return showAllFields || detail !== undefined
                      ? this.renderDetail(detail, key, label, isPrimaryList)
                      : null;
              })
            : [];
        const otherDetails = map(omit(details, baseKeyOrder), (detail, key) =>
            this.renderDetail(detail, key, this.formatLabel(key), isPrimaryList)
        );

        return [...orderedDetails, ...otherDetails];
    }

    renderPanelAction() {
        const {noDetailExpand} = this.props;

        return !noDetailExpand ? <PanelAction {...this.getPanelActionProps()} /> : null;
    }

    renderRecord(searchData) {
        const mediaItems = searchData.map(mediaItem => {
            const manualOuterCopy = {};
            Object.keys(mediaItem).forEach(key => {
                if (key === 'encodedMedia') {
                    manualOuterCopy[key] = mediaItem.encodedMedia.map(encoded => {
                        const manualCopy = {};
                        Object.keys(encoded).forEach(key => {
                            if (key !== 'duration') {
                                manualCopy[key] = encoded[key];
                            } else {
                                manualCopy.duration = encoded.duration / 1000;
                            }
                        });

                        return manualCopy;
                    });
                } else {
                    manualOuterCopy[key] = mediaItem[key];
                }
            });
            return manualOuterCopy;
        });

        return map(mediaItems, (details, key) => this.renderList(details, key, true));
    }

    render() {
        return (
            <div className={this.getClass()}>
                {this.renderPanelAction()}
                {this.renderHeader()}
                {this.renderContent()}
                {this.renderFooter()}
            </div>
        );
    }
}

RecordDetails.propTypes = {
    blacklistKeys: PropTypes.arrayOf(PropTypes.string),
    className: PropTypes.string,
    defaultExpandKeys: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.bool]),
    detailExpandIcon: PropTypes.string,
    detailStartRenderer: PropTypes.func,
    footerRenderer: PropTypes.func,
    headerRenderer: PropTypes.func,
    keyOrder: PropTypes.string,
    keyRenderers: PropTypes.object,
    noDetailExpand: PropTypes.bool,
    noTitleFormatting: PropTypes.bool,
    onClick: PropTypes.func,
    searchData: PropTypes.array.isRequired,
    whitelistKeys: PropTypes.arrayOf(PropTypes.string),
};
